import { useAppSelector } from '../../../../configuration/setup/hooks';
import { getUserAccount, getUserProfileId } from '../../../../configuration/login/loginSlice';
import { useFetchUserWithEmbedQuery } from '../../services/usersApi';
import type { MouseEvent } from 'react';
import { useIntl } from 'react-intl';
import { useFetchAccountNameQuery } from '../../services/accountsApi';

const displayAccountId =
    localStorage.getItem('displayAccountId')?.toLowerCase() === 'true' ||
    localStorage.getItem('displayAccountId')?.toLowerCase() === 'yes' ||
    localStorage.getItem('displayAccountId')?.toLowerCase() === 'y' ||
    localStorage.getItem('displayAccountId') === '1';

export const UserInNavbar = () => {
    const userProfileId = useAppSelector(getUserProfileId);
    const accountId = useAppSelector(getUserAccount);

    // biome-ignore lint/style/noNonNullAssertion: suppress out of convenience during migration to biome
    const { data: user } = useFetchUserWithEmbedQuery(userProfileId!);
    // biome-ignore lint/style/noNonNullAssertion: suppress out of convenience during migration to biome
    const { data: accountName } = useFetchAccountNameQuery({ accountId: accountId! });

    if (user === undefined) {
        return null;
    }

    const preferredLogin = user.email ? user.email : user.phoneNumber || '';
    return (
        <div className='margin-bottom-10 padding-y-10'>
            <div className='display-flex align-items-center flex-column gap-10 line-height-125rel text-color-dark text-size-14'>
                <div
                    className='ellipsis-1 max-width-250 text-size-16 text-bold'
                    title={`${user.firstName} ${user.lastName}`}
                >
                    {`${user.firstName} ${user.lastName}`}
                </div>
                <div className='ellipsis-1 max-width-250' title={preferredLogin}>
                    {preferredLogin}
                </div>
                {accountName && <CurrentAccount accountId={accountId} accountName={accountName} />}
                {accountId && displayAccountId && (
                    <div className='display-flex flex-wrap align-items-center justify-content-center'>
                        <span className='badge badge-muted' style={{ cursor: 'default' }} title={'Account ID'}>
                            {abbreviateString(accountId)}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

const CurrentAccount = ({ accountId, accountName }: { accountId: string | null; accountName: string }) => {
    const handleOnClick = (e: MouseEvent<HTMLElement>) => {
        e.preventDefault();
        copyToClipboard({ accountId, accountName });
    };

    const intl = useIntl();

    return (
        <div className='display-flex flex-wrap align-items-center justify-content-center'>
            {/** biome-ignore lint/a11y/noStaticElementInteractions: suppress out of convenience during migration to biome */}
            <div
                className='display-flex gap-5 badge badge-muted'
                style={{ cursor: 'pointer' }}
                onClick={handleOnClick}
                title={intl.formatMessage({ id: 'intl-msg:settingsDialog.accountInfo.tooltip' })}
            >
                <span className={'margin-left-3'}>{abbreviateString(accountName)}</span>
                <span className={'rioglyph rioglyph-clipboard margin-right-3'} />
            </div>
        </div>
    );
};

const copyToClipboard = (account: Record<string, any>) => {
    try {
        // Fallback: Create a temporary textarea element
        const textArea = document.createElement('textarea');
        textArea.value = JSON.stringify(account);
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
    } catch (err) {
        console.error('Failed to copy text: ', err);
    }
};

const abbreviateString = (input: string) => {
    const maxLength = 56;

    // Check if the string needs to be abbreviated
    if (input.length > maxLength) {
        // Find the last space before the 60 character limit
        const lastSpace = input.substring(0, maxLength).lastIndexOf(' ');

        // If there is no space, or the space is very early in the string, we truncate at maxLength
        if (lastSpace === -1 || lastSpace < 30) {
            // 30 is arbitrary, adjust based on your needs
            return `${input.substring(0, maxLength)} ...`;
        }

        // Return the string up to the last space and append ' ...'
        return `${input.substring(0, lastSpace)} ...`;
    }

    // Return the original string if it's shorter than maxLength
    return input;
};
